import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    Paper,
    Snackbar,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ArrowBack, Public, PublicOff, Save as SaveIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import { Logger } from 'utils/Logger';
import { useModulePermissions } from 'hooks/useModulePermissions';
import { ReelDialog } from './ReelDialog';
import { reelMakerApi } from './ReelMaker.api';
import { REEL_MAKER_PATH, Reel, ReelUpsertPayload, reelEditorPath, reelVersionLabel } from './ReelMaker.types';
import {
    ReelCanvasDocument,
    ReelElement,
    ReelElementType,
    applyDurationToElements,
    clampElementBounds,
    createReelElement,
    isCanvasElement,
    isElementActiveAt,
    normalizeReelCanvas,
} from './reelCanvas.types';
import { SideKickMenu } from './SideKickMenu';
import { CanvasElementView } from './CanvasElementView';
import { LayersPanel } from './LayersPanel';
import { REEL_TIMELINE_HEIGHT, REEL_TIMELINE_TOGGLE_HEIGHT, ReelTimeline } from './ReelTimeline';
import { MediaElementDialog } from './dialogs/MediaElementDialog';
import { TextElementDialog } from './dialogs/TextElementDialog';
import { ContainerElementDialog } from './dialogs/ContainerElementDialog';
import { AudioElementDialog } from './dialogs/AudioElementDialog';
import { AudioTrackPlayer } from './AudioTrackPlayer';

const LOG_PREFIX = 'components/ReelMaker/ReelEditor.tsx';

/** Padding kept around the scaled canvas inside the stage (px). */
const STAGE_PADDING = 24;

export interface ReelEditorProps {
    /** Breadcrumb label of the parent list (default "Reel Maker"). */
    listTitle?: string;
}

/**
 * Beam on Screen → Reel Maker → editor (`/#/beamonscreen/reel-maker/:reelId`).
 *
 * Stage = the reel canvas (intrinsic width × height, CSS-scaled to fit) plus the floating
 * SideKickMenu (add media / text / container), the Layers panel (z-order) and the bottom
 * Timeline (reel length + per-layer clip windows). The document is saved to `reel.canvas`.
 */
export const ReelEditor: React.FC<ReelEditorProps> = ({ listTitle = 'Reel Maker' }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { reelId = '' } = useParams<{ reelId: string }>();
    const { hasBeamonScreenAccess, isUserSuperAdmin } = useModulePermissions();
    const canManage = hasBeamonScreenAccess() || isUserSuperAdmin;

    const [reel, setReel] = useState<Reel | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [statusBusy, setStatusBusy] = useState(false);

    const [propsDialogOpen, setPropsDialogOpen] = useState(false);
    const [propsSaving, setPropsSaving] = useState(false);
    const [propsError, setPropsError] = useState<string | null>(null);

    // ---- canvas document ----
    const [doc, setDoc] = useState<ReelCanvasDocument>(() => normalizeReelCanvas(null));
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [layersOpen, setLayersOpen] = useState(false);
    const [timelineOpen, setTimelineOpen] = useState(false);
    const [playheadMs, setPlayheadMs] = useState(0);
    const [playing, setPlaying] = useState(false);
    /** Settings dialog for a SideKick component (opened by clicking it on the canvas or the timeline). */
    const [elementDialog, setElementDialog] = useState<{ id: string; startWithPicker: boolean } | null>(null);

    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });
    const showMessage = (message: string, severity: 'success' | 'error' = 'success') => setSnackbar({ open: true, message, severity });

    const stageRef = useRef<HTMLDivElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [stageSize, setStageSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

    const canvasSize = useMemo(() => ({ width: reel?.width ?? 0, height: reel?.height ?? 0 }), [reel?.width, reel?.height]);

    const loadReel = useCallback(async () => {
        if (!reelId) {
            setLoadError('Reel id is missing');
            setLoading(false);
            return;
        }
        setLoading(true);
        setLoadError(null);
        try {
            const data = await reelMakerApi.getReel(reelId);
            setReel(data);
            setDoc(normalizeReelCanvas(data.canvas));
            setDirty(false);
            setSelectedId(null);
            setPlayheadMs(0);
        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            Logger.error(LOG_PREFIX, `loadReel(${reelId}) failed: ${message}`);
            setLoadError(message);
        } finally {
            setLoading(false);
        }
    }, [reelId]);

    useEffect(() => {
        void loadReel();
    }, [loadReel]);

    // Track the stage size so the canvas can be scaled to fit.
    useLayoutEffect(() => {
        const el = stageRef.current;
        if (!el) return;
        const update = () => setStageSize({ width: el.clientWidth, height: el.clientHeight });
        update();
        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', update);
            return () => window.removeEventListener('resize', update);
        }
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [reel?._id]);

    const bottomReserved = timelineOpen ? REEL_TIMELINE_HEIGHT : REEL_TIMELINE_TOGGLE_HEIGHT;
    const scale = useMemo(() => {
        if (!reel || stageSize.width <= 0 || stageSize.height <= 0) return 1;
        const availW = Math.max(1, stageSize.width - STAGE_PADDING * 2);
        const availH = Math.max(1, stageSize.height - STAGE_PADDING * 2 - bottomReserved);
        return Math.min(1, availW / reel.width, availH / reel.height);
    }, [reel, stageSize, bottomReserved]);

    // Background paint (colour + subtle grid) — layers are DOM overlays above this surface.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !reel) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const { width, height } = reel;
        ctx.clearRect(0, 0, width, height);
        ctx.fillStyle = doc.backgroundColor || (theme.palette.mode === 'dark' ? '#111318' : '#ffffff');
        ctx.fillRect(0, 0, width, height);
        const step = Math.max(32, Math.round(Math.min(width, height) / 12));
        ctx.strokeStyle = 'rgba(255,255,255,0.06)';
        ctx.lineWidth = 1;
        for (let x = step; x < width; x += step) {
            ctx.beginPath();
            ctx.moveTo(x + 0.5, 0);
            ctx.lineTo(x + 0.5, height);
            ctx.stroke();
        }
        for (let y = step; y < height; y += step) {
            ctx.beginPath();
            ctx.moveTo(0, y + 0.5);
            ctx.lineTo(width, y + 0.5);
            ctx.stroke();
        }
    }, [reel, doc.backgroundColor, theme.palette.mode]);

    // ---- playback ----
    useEffect(() => {
        if (!playing) return;
        let raf = 0;
        let last = performance.now();
        const tick = (now: number) => {
            const dt = now - last;
            last = now;
            setPlayheadMs((p) => {
                const next = p + dt;
                return next >= doc.durationMs ? next % doc.durationMs : next;
            });
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [playing, doc.durationMs]);

    // ---- document mutations ----
    const mutate = useCallback((fn: (d: ReelCanvasDocument) => ReelCanvasDocument, markDirty = true) => {
        setDoc((d) => fn(d));
        if (markDirty) setDirty(true);
    }, []);

    const updateElement = useCallback(
        (id: string, patch: Partial<ReelElement> | ((el: ReelElement) => ReelElement), markDirty = true) =>
            mutate(
                (d) => ({
                    ...d,
                    elements: d.elements.map((el) =>
                        el.id === id ? (typeof patch === 'function' ? patch(el) : ({ ...el, ...patch } as ReelElement)) : el,
                    ),
                }),
                markDirty,
            ),
        [mutate],
    );

    const handleAdd = (type: ReelElementType) => {
        if (!reel) return;
        const el = createReelElement(type, canvasSize, doc);
        mutate((d) => ({ ...d, elements: [...d.elements, el] }));
        setSelectedId(el.id);
        if (playheadMs < el.startMs || playheadMs >= el.endMs) setPlayheadMs(el.startMs);
        if (type === 'audio') {
            // Audio lives on the timeline only: reveal it and go straight to the audio picker.
            setTimelineOpen(true);
            setPlaying(false);
            setElementDialog({ id: el.id, startWithPicker: true });
        }
    };

    const handleMoveLayer = (id: string, direction: 'up' | 'down' | 'top' | 'bottom') =>
        mutate((d) => {
            const idx = d.elements.findIndex((e) => e.id === id);
            if (idx < 0) return d;
            const next = [...d.elements];
            const [el] = next.splice(idx, 1);
            const target =
                direction === 'top' ? next.length : direction === 'bottom' ? 0 : direction === 'up' ? Math.min(next.length, idx + 1) : Math.max(0, idx - 1);
            next.splice(target, 0, el);
            return { ...d, elements: next };
        });

    const handleDelete = (id: string) => {
        mutate((d) => ({ ...d, elements: d.elements.filter((e) => e.id !== id) }));
        setSelectedId((s) => (s === id ? null : s));
    };

    const handleDurationChange = (ms: number) => {
        mutate((d) => ({ ...d, durationMs: ms, elements: applyDurationToElements(d.elements, ms) }));
        setPlayheadMs((p) => Math.min(p, ms));
    };

    const handleClipChange = (id: string, startMs: number, endMs: number) => updateElement(id, { startMs, endMs }, false);

    /** Media without an asset opens straight into the picker; everything else opens its settings. */
    const openElementDialog = (id: string) => {
        const el = doc.elements.find((e) => e.id === id);
        if (!el) return;
        setSelectedId(id);
        setPlaying(false);
        setElementDialog({ id, startWithPicker: (el.type === 'media' || el.type === 'audio') && !el.assetUrl });
    };
    const dialogElement = elementDialog ? doc.elements.find((e) => e.id === elementDialog.id) ?? null : null;

    // ---- save ----
    const handleSave = useCallback(async () => {
        if (!reel || saving) return;
        setSaving(true);
        try {
            const updated = await reelMakerApi.updateReel(reel._id, { canvas: doc });
            setReel(updated);
            setDirty(false);
            showMessage(`Canvas saved (${reelVersionLabel(updated)})`);
        } catch (error) {
            showMessage(`Failed to save canvas: ${error instanceof Error ? error.message : String(error)}`, 'error');
        } finally {
            setSaving(false);
        }
    }, [reel, saving, doc]);

    // Ctrl/Cmd+S saves, Delete removes the selection, Escape deselects, Space toggles playback.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement | null;
            const typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
                e.preventDefault();
                if (dirty && canManage) void handleSave();
                return;
            }
            if (typing) return;
            if (elementDialog || propsDialogOpen) return;
            if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId && canManage) {
                const el = doc.elements.find((x) => x.id === selectedId);
                if (el && !el.locked) {
                    e.preventDefault();
                    handleDelete(selectedId);
                }
            } else if (e.key === 'Escape') {
                setSelectedId(null);
            } else if (e.key === ' ' && timelineOpen) {
                e.preventDefault();
                setPlaying((p) => !p);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dirty, canManage, selectedId, doc.elements, timelineOpen, handleSave, elementDialog, propsDialogOpen]);

    // Warn before leaving the page with unsaved changes.
    useEffect(() => {
        if (!dirty) return;
        const onBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [dirty]);

    // ---- status / props ----
    const handleToggleStatus = async () => {
        if (!reel) return;
        const wantsPublish = reel.status !== 'published' || reel.hasDraft || dirty;
        const next = wantsPublish ? 'published' : 'unpublished';
        setStatusBusy(true);
        try {
            // Publishing freezes the working copy: save pending edits first so the version matches the canvas.
            if (wantsPublish && dirty) {
                await reelMakerApi.updateReel(reel._id, { canvas: doc });
                setDirty(false);
            }
            const updated = await reelMakerApi.setReelStatus(reel._id, next);
            setReel((r) => (r ? { ...r, ...updated, canvas: r.canvas } : updated));
            showMessage(
                next === 'published'
                    ? `Reel published${updated.publishedLabel ? ` as ${updated.publishedLabel}` : ''}`
                    : 'Reel unpublished',
            );
        } catch (error) {
            showMessage(`Failed to update status: ${error instanceof Error ? error.message : String(error)}`, 'error');
        } finally {
            setStatusBusy(false);
        }
    };

    const handleSaveProps = async (payload: ReelUpsertPayload) => {
        if (!reel) return;
        setPropsSaving(true);
        setPropsError(null);
        try {
            const updated = await reelMakerApi.updateReel(reel._id, payload);
            setReel((r) => (r ? { ...r, ...updated, canvas: r.canvas } : updated));
            // Keep every layer inside the (possibly smaller) canvas; flag as unsaved only if something moved.
            const size = { width: updated.width, height: updated.height };
            const clamped = doc.elements.map((el) => clampElementBounds(el, size));
            const changed = clamped.some((el, i) => el !== doc.elements[i] && JSON.stringify(el) !== JSON.stringify(doc.elements[i]));
            if (changed) mutate((d) => ({ ...d, elements: d.elements.map((el) => clampElementBounds(el, size)) }));
            setPropsDialogOpen(false);
            showMessage('Reel updated');
        } catch (error) {
            setPropsError(error instanceof Error ? error.message : String(error));
        } finally {
            setPropsSaving(false);
        }
    };

    const goBack = () => {
        if (dirty && !window.confirm('You have unsaved canvas changes. Leave without saving?')) return;
        navigate(REEL_MAKER_PATH);
    };

    const breadcrumbs = [
        { itemPath: '/dashboard', itemLabel: 'Home' },
        { itemPath: REEL_MAKER_PATH, itemLabel: listTitle },
        { itemPath: reelEditorPath(reelId), itemLabel: reel?.name ?? 'Reel' },
    ];

    const selectedElement = doc.elements.find((e) => e.id === selectedId) ?? null;

    /** `v3-draft` while a draft exists or unsaved edits would start one; `v2` when fully published. */
    const versionChip = reel
        ? reel.hasDraft || (reel.publishedVersion ?? 0) === 0
            ? reelVersionLabel(reel)
            : dirty
              ? `v${(reel.publishedVersion ?? 0) + 1}-draft`
              : reelVersionLabel(reel)
        : '';
    const publishLabel = reel
        ? reel.hasDraft || dirty || (reel.publishedVersion ?? 0) === 0
            ? `Publish v${reel.hasDraft ? reel.draftVersion : (reel.publishedVersion ?? 0) + 1}`
            : 'Publish'
        : 'Publish';

    return (
        <Box
            pl={{ xs: 1.25, md: 2.5 }}
            pt={1}
            width="100%"
            mb={3}
            sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 100px - 32px)' }}
        >
            <DynamicBreadcrumbs items={breadcrumbs} />

            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Tooltip title={`Back to ${listTitle}`}>
                    <IconButton onClick={goBack} aria-label={`Back to ${listTitle}`} color="secondary" sx={{ border: '1px solid', borderColor: 'divider' }}>
                        <ArrowBack />
                    </IconButton>
                </Tooltip>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="h5" noWrap sx={{ fontWeight: 700 }}>
                        {reel?.name ?? (loading ? 'Loading…' : 'Reel')}
                    </Typography>
                    {reel && (
                        <Typography variant="body2" color="text.secondary" noWrap>
                            {reel.width} × {reel.height} px · {doc.elements.length} layer{doc.elements.length === 1 ? '' : 's'}
                            {doc.elements.some((e) => e.type === 'audio') ? ` (${doc.elements.filter((e) => e.type === 'audio').length} audio)` : ''}
                            {selectedElement ? ` · Selected: ${selectedElement.name}` : ''}
                            {reel.description ? ` · ${reel.description}` : ''}
                        </Typography>
                    )}
                </Box>
                {reel && (
                    <Chip
                        label={reel.status}
                        color={reel.status === 'published' ? 'success' : 'warning'}
                        size="small"
                        sx={{ textTransform: 'capitalize', fontWeight: 700 }}
                    />
                )}
                {reel && (
                    <Tooltip title={reel.hasDraft ? 'Working draft — publish to freeze it as a version' : reel.publishedLabel ? `Last published ${reel.publishedLabel}` : 'Not published yet'}>
                        <Chip label={versionChip} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontWeight: 700 }} />
                    </Tooltip>
                )}
                {reel && dirty && <Chip label="Unsaved changes" size="small" variant="outlined" color="warning" />}
                {reel && canManage && (
                    <>
                        <Button
                            variant="outlined"
                            color="secondary"
                            size="small"
                            startIcon={<SettingsIcon />}
                            onClick={() => {
                                setPropsError(null);
                                setPropsDialogOpen(true);
                            }}
                        >
                            Properties
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                            disabled={!dirty || saving}
                            onClick={() => void handleSave()}
                        >
                            Save
                        </Button>
                        <Button
                            variant="contained"
                            color={reel.status === 'published' ? 'warning' : 'success'}
                            size="small"
                            startIcon={statusBusy ? <CircularProgress size={14} color="inherit" /> : reel.status === 'published' ? <PublicOff /> : <Public />}
                            disabled={statusBusy}
                            onClick={() => void handleToggleStatus()}
                        >
                            {reel.status === 'published' && !reel.hasDraft && !dirty ? 'Unpublish' : publishLabel}
                        </Button>
                    </>
                )}
            </Box>

            {/* Stage */}
            <Paper
                ref={stageRef}
                variant="outlined"
                onPointerDown={() => setSelectedId(null)}
                sx={{
                    flex: 1,
                    minHeight: 320,
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.04)',
                    backgroundImage:
                        theme.palette.mode === 'dark'
                            ? 'radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)'
                            : 'radial-gradient(rgba(0,0,0,0.10) 1px, transparent 1px)',
                    backgroundSize: '16px 16px',
                }}
            >
                {loading && (
                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CircularProgress color="secondary" />
                    </Box>
                )}
                {!loading && loadError && (
                    <Box sx={{ textAlign: 'center', p: 3, alignSelf: 'center' }}>
                        <Typography color="error" sx={{ mb: 1 }}>
                            {loadError}
                        </Typography>
                        <Button variant="outlined" color="secondary" onClick={goBack} startIcon={<ArrowBack />}>
                            Back to {listTitle}
                        </Button>
                    </Box>
                )}
                {!loading && reel && (
                    <>
                        <SideKickMenu
                            onAdd={handleAdd}
                            layersOpen={layersOpen}
                            onToggleLayers={() => setLayersOpen((v) => !v)}
                            timelineOpen={timelineOpen}
                            onToggleTimeline={() => setTimelineOpen((v) => !v)}
                            disabled={!canManage}
                            boundsRef={stageRef}
                        />

                        {layersOpen && (
                            <LayersPanel
                                elements={doc.elements}
                                selectedId={selectedId}
                                onSelect={setSelectedId}
                                onMove={handleMoveLayer}
                                onToggleVisible={(id) => updateElement(id, (el) => ({ ...el, visible: !el.visible }))}
                                onToggleLocked={(id) => updateElement(id, (el) => ({ ...el, locked: !el.locked }))}
                                onDelete={handleDelete}
                                onClose={() => setLayersOpen(false)}
                            />
                        )}

                        {/* Scaled canvas + layer overlay, vertically centred in the space above the timeline */}
                        <Box
                            sx={{
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                top: 0,
                                bottom: bottomReserved,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                pointerEvents: 'none',
                            }}
                        >
                            <Box
                                sx={{
                                    width: reel.width * scale,
                                    height: reel.height * scale,
                                    position: 'relative',
                                    pointerEvents: 'auto',
                                    boxShadow: `0 8px 32px ${alpha('#000', theme.palette.mode === 'dark' ? 0.6 : 0.25)}`,
                                }}
                            >
                                <Box
                                    onPointerDown={(e) => {
                                        e.stopPropagation();
                                        setSelectedId(null);
                                    }}
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: reel.width,
                                        height: reel.height,
                                        transform: `scale(${scale})`,
                                        transformOrigin: 'top left',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <canvas
                                        ref={canvasRef}
                                        width={reel.width}
                                        height={reel.height}
                                        aria-label={`${reel.name} canvas ${reel.width} by ${reel.height} pixels`}
                                        style={{ position: 'absolute', top: 0, left: 0, width: reel.width, height: reel.height, display: 'block' }}
                                    />
                                    {doc.elements.map((el, index) =>
                                        isCanvasElement(el) && isElementActiveAt(el, playheadMs) ? (
                                            <CanvasElementView
                                                key={el.id}
                                                element={el}
                                                zIndex={index + 1}
                                                selected={el.id === selectedId}
                                                scale={scale}
                                                canvas={canvasSize}
                                                readOnly={!canManage}
                                                playheadMs={playheadMs}
                                                playing={playing}
                                                onSelect={setSelectedId}
                                                onChange={(next) => updateElement(next.id, next, false)}
                                                onCommit={() => setDirty(true)}
                                                onOpen={(target) => openElementDialog(target.id)}
                                            />
                                        ) : null,
                                    )}
                                </Box>
                            </Box>
                        </Box>

                        <Chip
                            size="small"
                            label={`${reel.width} × ${reel.height} px · ${Math.round(scale * 100)}%`}
                            sx={{
                                position: 'absolute',
                                right: 12,
                                bottom: bottomReserved + 12,
                                fontWeight: 600,
                                zIndex: 20,
                                backgroundColor: alpha(theme.palette.background.paper, 0.9),
                            }}
                        />

                        <ReelTimeline
                            open={timelineOpen}
                            onToggle={() => setTimelineOpen((v) => !v)}
                            elements={doc.elements}
                            selectedId={selectedId}
                            onSelect={setSelectedId}
                            durationMs={doc.durationMs}
                            onDurationChange={handleDurationChange}
                            playheadMs={playheadMs}
                            onSeek={(ms) => {
                                setPlaying(false);
                                setPlayheadMs(ms);
                            }}
                            playing={playing}
                            onTogglePlay={() => setPlaying((p) => !p)}
                            onClipChange={handleClipChange}
                            onClipCommit={() => setDirty(true)}
                            onOpenElement={openElementDialog}
                            readOnly={!canManage}
                            containerRef={stageRef}
                        />
                    </>
                )}
            </Paper>

            {/* Timeline-only audio layers (hidden players synced to the transport) */}
            {doc.elements.map((el) =>
                el.type === 'audio' ? (
                    <AudioTrackPlayer key={el.id} element={el} playheadMs={playheadMs} playing={playing} muted={Boolean(elementDialog)} />
                ) : null,
            )}

            {/* SideKick component settings */}
            <AudioElementDialog
                open={Boolean(elementDialog) && dialogElement?.type === 'audio'}
                element={dialogElement?.type === 'audio' ? dialogElement : null}
                startWithPicker={elementDialog?.startWithPicker ?? false}
                onClose={() => setElementDialog(null)}
                onChange={(patch) => dialogElement && updateElement(dialogElement.id, patch as Partial<ReelElement>)}
            />
            <MediaElementDialog
                open={Boolean(elementDialog) && dialogElement?.type === 'media'}
                element={dialogElement?.type === 'media' ? dialogElement : null}
                startWithPicker={elementDialog?.startWithPicker ?? false}
                onClose={() => setElementDialog(null)}
                onChange={(patch) => dialogElement && updateElement(dialogElement.id, patch as Partial<ReelElement>)}
            />
            <TextElementDialog
                open={Boolean(elementDialog) && dialogElement?.type === 'text'}
                element={dialogElement?.type === 'text' ? dialogElement : null}
                onClose={() => setElementDialog(null)}
                onChange={(patch) => dialogElement && updateElement(dialogElement.id, patch as Partial<ReelElement>)}
            />
            <ContainerElementDialog
                open={Boolean(elementDialog) && dialogElement?.type === 'container'}
                element={dialogElement?.type === 'container' ? dialogElement : null}
                onClose={() => setElementDialog(null)}
                onChange={(patch) => dialogElement && updateElement(dialogElement.id, patch as Partial<ReelElement>)}
            />

            <ReelDialog
                open={propsDialogOpen}
                reel={reel}
                saving={propsSaving}
                error={propsError}
                onClose={() => !propsSaving && setPropsDialogOpen(false)}
                onSave={handleSaveProps}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ReelEditor;
